import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Video, Plus, FileText, Phone, PhoneOff, Calendar, Clock, MapPin, User, Users } from "lucide-react";
import JitsiMeetEmbed from "./JitsiMeetEmbed";
import MeetingMinutesDialog from "./MeetingMinutesDialog";
import MeetingSetupDialog from "./MeetingSetupDialog";
import { format } from "date-fns";

export default function MeetingRoom() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState<any>(null);
  const [jitsiOpen, setJitsiOpen] = useState(false);
  const [minutesMeeting, setMinutesMeeting] = useState<any>(null);
  const [creatingInstant, setCreatingInstant] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [profilesMap, setProfilesMap] = useState<Record<string, string>>({});

  useEffect(() => {
    loadMeetings();
    loadProfile();
  }, []);

  const loadProfile = async () => {
    if (!user) return;
    const { data } = await supabase.from("profiles").select("full_name, salutation").eq("user_id", user.id).maybeSingle();
    if (data) {
      setDisplayName([data.salutation, data.full_name].filter(Boolean).join(" ") || user.email || "Participant");
    }
  };

  const startInstantMeeting = async () => {
    if (!user) return;
    setCreatingInstant(true);
    const roomName = `stdavid-${crypto.randomUUID().slice(0, 8)}`;
    const { data, error } = await supabase.from("meetings").insert({
      title: `Instant Meeting – ${format(new Date(), "PPp")}`,
      room_name: roomName,
      created_by: user.id,
      status: "active",
      started_at: new Date().toISOString(),
      meeting_type: "regular",
      recording_enabled: true,
      transcription_enabled: true,
    } as any).select().single();
    if (error) {
      toast.error(error.message);
    } else if (data) {
      await supabase.from("meeting_participants").insert({
        meeting_id: data.id,
        user_id: user.id,
        display_name: displayName,
        role: "creator",
      });
      setActiveMeeting(data);
      setJitsiOpen(true);
      loadMeetings();
    }
    setCreatingInstant(false);
  };
  const loadMeetings = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("meetings")
      .select("*")
      .in("status", ["scheduled", "active", "ended"])
      .order("created_at", { ascending: false })
      .limit(50);
    const meetingData = data || [];
    setMeetings(meetingData);

    // Load profile names for chair/secretary
    const userIds = new Set<string>();
    meetingData.forEach((m: any) => {
      if (m.chair_user_id) userIds.add(m.chair_user_id);
      if (m.secretary_user_id) userIds.add(m.secretary_user_id);
    });
    if (userIds.size > 0) {
      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, full_name, salutation")
        .in("user_id", Array.from(userIds));
      const map: Record<string, string> = {};
      (profiles || []).forEach((p: any) => {
        map[p.user_id] = [p.salutation, p.full_name].filter(Boolean).join(" ") || "Unknown";
      });
      setProfilesMap(map);
    }
    setLoading(false);
  };

  const joinMeeting = async (meeting: any) => {
    if (!user) return;
    if (meeting.status === "scheduled") {
      await supabase.from("meetings").update({
        status: "active",
        started_at: new Date().toISOString(),
      }).eq("id", meeting.id);
    }
    const { data: roles } = await supabase.rpc("get_user_roles", { _user_id: user.id });
    await supabase.from("meeting_participants").insert({
      meeting_id: meeting.id,
      user_id: user.id,
      display_name: displayName,
      role: (roles as string[] | null)?.[0] || "member",
    });
    setActiveMeeting(meeting);
    setJitsiOpen(true);
  };

  const leaveMeeting = async () => {
    if (activeMeeting && user) {
      await supabase.from("meeting_participants")
        .update({ left_at: new Date().toISOString() })
        .eq("meeting_id", activeMeeting.id)
        .eq("user_id", user.id)
        .is("left_at", null);
    }
    setJitsiOpen(false);
    setActiveMeeting(null);
    loadMeetings();
  };

  const endMeeting = async (meeting: any) => {
    await supabase.from("meetings").update({
      status: "ended",
      ended_at: new Date().toISOString(),
    }).eq("id", meeting.id);
    toast.success("Meeting ended");
    loadMeetings();
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "active": return "default";
      case "scheduled": return "secondary";
      case "ended": return "outline";
      default: return "secondary";
    }
  };

  const meetingTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      regular: "Regular", emergency: "Emergency", board: "Board",
      committee: "Committee", agm: "AGM", departmental: "Departmental",
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Meeting Rooms</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="default" onClick={startInstantMeeting} disabled={creatingInstant}>
            <Video className="h-4 w-4 mr-1" /> {creatingInstant ? "Starting..." : "Instant Meeting"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
            <Calendar className="h-4 w-4 mr-1" /> Schedule Meeting
          </Button>
        </div>
      </div>

      <MeetingSetupDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={loadMeetings} />

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading meetings...</div>
      ) : meetings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Video className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No meetings yet. Create one to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {meetings.map((meeting) => (
            <Card key={meeting.id} className="relative">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-sm leading-tight">{meeting.title}</CardTitle>
                  <div className="flex gap-1 shrink-0">
                    {meeting.meeting_type && meeting.meeting_type !== "regular" && (
                      <Badge variant="outline" className="text-[10px]">{meetingTypeLabel(meeting.meeting_type)}</Badge>
                    )}
                    <Badge variant={statusColor(meeting.status)} className="text-[10px]">
                      {meeting.status}
                    </Badge>
                  </div>
                </div>
                {meeting.department && (
                  <p className="text-xs text-muted-foreground">{meeting.department}</p>
                )}
              </CardHeader>
              <CardContent className="space-y-2">
                {meeting.scheduled_at && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {format(new Date(meeting.scheduled_at), "PPp")}
                  </div>
                )}
                {meeting.location && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    {meeting.location}
                  </div>
                )}
                {(meeting.chair_user_id || meeting.secretary_user_id) && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {meeting.chair_user_id && profilesMap[meeting.chair_user_id] && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" /> Chair: {profilesMap[meeting.chair_user_id]}
                      </span>
                    )}
                    {meeting.secretary_user_id && profilesMap[meeting.secretary_user_id] && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" /> Sec: {profilesMap[meeting.secretary_user_id]}
                      </span>
                    )}
                  </div>
                )}
                {meeting.started_at && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    Started: {format(new Date(meeting.started_at), "p")}
                    {meeting.ended_at && ` — Ended: ${format(new Date(meeting.ended_at), "p")}`}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-1">
                  {meeting.status !== "ended" && (
                    <Button size="sm" className="flex-1" onClick={() => joinMeeting(meeting)}>
                      <Phone className="h-3 w-3 mr-1" /> Join
                    </Button>
                  )}
                  {meeting.created_by === user?.id && meeting.status === "active" && (
                    <Button size="sm" variant="destructive" onClick={() => endMeeting(meeting)}>
                      <PhoneOff className="h-3 w-3 mr-1" /> End
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setMinutesMeeting(meeting)}>
                    <FileText className="h-3 w-3 mr-1" /> Minutes
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeMeeting && (
        <JitsiMeetEmbed
          open={jitsiOpen}
          onClose={leaveMeeting}
          roomName={activeMeeting.room_name}
          displayName={displayName}
          meetingTitle={activeMeeting.title}
          meetingId={activeMeeting.id}
          isChairOrSecretary={
            activeMeeting.chair_user_id === user?.id ||
            activeMeeting.secretary_user_id === user?.id ||
            activeMeeting.created_by === user?.id
          }
        />
      )}

      {minutesMeeting && (
        <MeetingMinutesDialog
          open={!!minutesMeeting}
          onClose={() => setMinutesMeeting(null)}
          meetingId={minutesMeeting.id}
          meetingTitle={minutesMeeting.title}
          isCreator={minutesMeeting.created_by === user?.id}
        />
      )}
    </div>
  );
}
